import { createContext, useContext, type ReactNode } from 'react'
import { cn } from '../lib/cn'
import { useDomId } from '../lib/useId'

interface FieldContextValue {
  inputId: string
  descriptionId?: string
  errorId?: string
  invalid: boolean
  required: boolean
}

const FieldContext = createContext<FieldContextValue | null>(null)

/**
 * Wires label / hint / error to the control via aria-describedby and
 * aria-invalid. Consumers get the correct relationships by construction, which
 * is what keeps a 5-step wizard with ~40 fields (BB-FR-001) axe-clean.
 */
export function useFieldControlProps() {
  const ctx = useContext(FieldContext)
  if (!ctx) return {}
  const describedBy = [ctx.descriptionId, ctx.errorId].filter(Boolean).join(' ') || undefined
  return {
    id: ctx.inputId,
    'aria-describedby': describedBy,
    'aria-invalid': ctx.invalid || undefined,
    'aria-required': ctx.required || undefined,
    required: ctx.required || undefined,
  } as const
}

export interface FieldProps {
  label: string
  /** Persistent helper text. Prefer this over a placeholder, which disappears on input. */
  hint?: ReactNode
  /** Presence of an error switches the control to the invalid treatment. */
  error?: string
  required?: boolean
  /** Visually hides the label but keeps it for assistive tech (search fields, table filters). */
  hideLabel?: boolean
  id?: string
  className?: string
  children: ReactNode
}

export function Field({
  label,
  hint,
  error,
  required = false,
  hideLabel = false,
  id,
  className,
  children,
}: FieldProps) {
  const inputId = useDomId('field', id)
  const descriptionId = hint ? `${inputId}-hint` : undefined
  const errorId = error ? `${inputId}-error` : undefined

  return (
    <FieldContext.Provider
      value={{ inputId, descriptionId, errorId, invalid: Boolean(error), required }}
    >
      <div className={cn('flex flex-col gap-1.5', className)}>
        {/* The required marker sits OUTSIDE <label> deliberately. Inside, it
            becomes part of the label's text content, so the control's accessible
            name turns into "Work email *" — which screen readers announce
            literally, and which makes every getByLabelText query in every test
            depend on punctuation. Requiredness is conveyed by aria-required on
            the control itself (see useFieldControlProps). */}
        <div className={cn('flex items-center gap-1', hideLabel && 'sr-only')}>
          <label htmlFor={inputId} className="text-body-sm font-medium text-text-primary">
            {label}
          </label>
          {required && (
            <span className="text-state-critical" aria-hidden>
              *
            </span>
          )}
        </div>

        {children}

        {hint && !error && (
          <p id={descriptionId} className="text-caption text-text-secondary">
            {hint}
          </p>
        )}
        {error && (
          <p
            id={errorId}
            /* Errors arriving after submit must be announced, not just painted. */
            role="alert"
            className="text-caption text-state-critical"
          >
            {error}
          </p>
        )}
      </div>
    </FieldContext.Provider>
  )
}
